import { useEffect, useRef, useState } from 'react';
import { Loader2, Minus, Plus } from 'lucide-react';

/**
 * Viewer PDF basato su pdf.js: renderizza le pagine su canvas a larghezza
 * piena del container, con zoom +/− e scroll verticale nativo.
 *
 * Nasce per l'anteprima contratto nella pagina di firma: l'iframe PDF non
 * scrolla su iOS (renderizza solo la prima pagina) e non permette zoom
 * controllato. I canvas funzionano identici su web e mobile.
 */
interface PdfZoomViewerProps {
  /** Object URL (blob) o URL del PDF da renderizzare. */
  pdfUrl: string;
}

const ZOOM_LEVELS = [0.75, 1, 1.25, 1.5, 2, 3];

export function PdfZoomViewer({ pdfUrl }: PdfZoomViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const pagesRef = useRef<HTMLDivElement>(null);
  const [zoomIdx, setZoomIdx] = useState(1); // default 1 = 100% (fit width)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [numPages, setNumPages] = useState(0);
  // Il PDFDocumentProxy vive in un ref per ri-renderizzare al cambio zoom
  // senza ricaricare il documento.
  const pdfDocRef = useRef<any>(null);

  const zoom = ZOOM_LEVELS[zoomIdx];

  // Carica il documento una volta
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const pdfjs = await import('pdfjs-dist');
        const workerUrl = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default;
        pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
        // getDocument v6 non accetta i blob: URL come stringa — passiamo i bytes
        const res = await fetch(pdfUrl);
        const data = await res.arrayBuffer();
        const doc = await pdfjs.getDocument({ data }).promise;
        if (cancelled) { doc.destroy(); return; }
        pdfDocRef.current = doc;
        setNumPages(doc.numPages);
      } catch (err) {
        console.error('[PdfZoomViewer] load error:', err);
        if (!cancelled) setError('Impossibile caricare il documento.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      pdfDocRef.current?.destroy?.();
      pdfDocRef.current = null;
    };
  }, [pdfUrl]);

  // Renderizza le pagine quando documento pronto o zoom cambia
  useEffect(() => {
    const doc = pdfDocRef.current;
    const host = pagesRef.current;
    const container = containerRef.current;
    if (!doc || !host || !container || numPages === 0) return;

    let cancelled = false;
    (async () => {
      host.innerHTML = '';
      // Larghezza disponibile del drawer (meno un piccolo margine scroll)
      const baseWidth = Math.max(container.clientWidth - 8, 280);
      const dpr = window.devicePixelRatio || 1;

      for (let i = 1; i <= doc.numPages; i++) {
        if (cancelled) return;
        const page = await doc.getPage(i);
        const unscaled = page.getViewport({ scale: 1 });
        // scale: pagina a larghezza piena del container × livello di zoom
        const scale = (baseWidth / unscaled.width) * zoom;
        const viewport = page.getViewport({ scale });

        const canvas = document.createElement('canvas');
        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;
        canvas.className = 'block bg-white shadow-sm mb-3 mx-auto';

        const ctx = canvas.getContext('2d');
        if (!ctx) continue;
        host.appendChild(canvas);
        await page.render({
          canvasContext: ctx,
          viewport,
          transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : undefined,
        }).promise;
      }
    })();
    return () => { cancelled = true; };
  }, [numPages, zoom]);

  if (error) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-[#787774]">{error}</div>
    );
  }

  return (
    <div className="relative h-full flex flex-col">
      {/* Barra zoom in una riga sua, sopra le pagine: sovrapposta copriva
          l'intestazione della prima pagina (numero e tipo di contratto). */}
      <div className="shrink-0 flex justify-end border-b border-black/[0.06] dark:border-white/10 bg-white dark:bg-gray-800 px-3 py-1">
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => setZoomIdx((i) => Math.max(0, i - 1))}
          disabled={zoomIdx === 0}
          className="p-1.5 rounded text-[#37352F] dark:text-white hover:bg-black/[0.05] dark:hover:bg-white/10 disabled:opacity-30 transition-colors"
          aria-label="Riduci zoom"
        >
          <Minus className="w-4 h-4" />
        </button>
        <span className="min-w-[3rem] text-center text-xs font-medium text-[#37352F] dark:text-white tabular-nums">
          {Math.round(zoom * 100)}%
        </span>
        <button
          type="button"
          onClick={() => setZoomIdx((i) => Math.min(ZOOM_LEVELS.length - 1, i + 1))}
          disabled={zoomIdx === ZOOM_LEVELS.length - 1}
          className="p-1.5 rounded text-[#37352F] dark:text-white hover:bg-black/[0.05] dark:hover:bg-white/10 disabled:opacity-30 transition-colors"
          aria-label="Aumenta zoom"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
      </div>

      {/* Area pagine: scroll verticale + orizzontale quando zoom > fit */}
      <div
        ref={containerRef}
        className="flex-1 overflow-auto overscroll-contain bg-[#F1F0EE] dark:bg-gray-900 px-1 py-3"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-[#787774]" />
          </div>
        )}
        <div ref={pagesRef} />
      </div>
    </div>
  );
}
